import { SectionEditor } from "@/components/sections/SectionEditor";
import { SECTION_LABELS } from "@/components/sections/constants";
import { X, Trash2, Copy, Save, History, Undo2 } from "lucide-react";

// Curated Google Fonts. Loaded on-demand from Google's CSS API only when the
// user picks a non-default. Small list keeps the picker snappy and on-brand.
export const CURATED_FONTS = [
  "Inter", "Poppins", "Playfair Display", "Lora",
  "Space Grotesk", "Manrope", "DM Serif Display", "Plus Jakarta Sans",
];
const SIZE_SCALES = [
  { k: "s", label: "S · compact" },
  { k: "m", label: "M · default" },
  { k: "l", label: "L · airy" },
];

// Right-side inspector. Shows the SectionEditor for the currently selected
// section OR a "page settings" panel (title / accent color / SEO shortcut)
// when nothing is selected.
export function PropertiesInspector({
  page,
  selectedId,
  onChange,          // (nextSection) => void — commits a section update to history
  onPageChange,      // (patch) => void — commits page-level changes (title etc.)
  onDeselect,
  onDelete,
  onDuplicate,
  onSaveAsTemplate,  // () => void — open save-as-template modal for selected
  onRestoreAiVersion,// (historyIdx) => void — restore a past AI snapshot
  onOpenSeo,
}) {
  const selected = selectedId ? page.sections.find((s) => s.id === selectedId) : null;
  const history = selected?.ai_history || [];

  return (
    <aside className="w-80 shrink-0 border-l border-[#E4E5E9] bg-white flex flex-col" data-testid="properties-inspector">
      {selected ? (
        <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E5E9]">
            <div>
              <div className="vp-label" style={{ fontSize: "0.65rem" }}>Editing</div>
              <div className="font-semibold text-sm text-[#0B0D17]">{SECTION_LABELS[selected.kind] || selected.kind}</div>
            </div>
            <button
              type="button"
              onClick={onDeselect}
              className="p-1.5 rounded-md text-[#4A4D57] hover:bg-[#F1F3F7]"
              title="Close inspector"
              data-testid="inspector-close"
            >
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            <SectionEditor section={selected} onChange={onChange} />

            {/* AI rewrite history — appears only when a section has past versions */}
            {history.length > 0 && (
              <div className="mt-6 pt-4 border-t border-[#E4E5E9]" data-testid="ai-history-panel">
                <div className="flex items-center gap-1.5 mb-2">
                  <History className="w-3.5 h-3.5 text-[#0B5CFF]" strokeWidth={1.75} />
                  <div className="vp-label" style={{ fontSize: "0.65rem" }}>AI rewrite history · {history.length}</div>
                </div>
                <p className="text-[11px] text-[#747680] mb-3 leading-relaxed">
                  Har rewrite se pehle ka snapshot yahaan save hota hai (last 5). Click to restore.
                </p>
                <div className="space-y-2">
                  {history.slice().reverse().map((h, revIdx) => {
                    const idx = history.length - 1 - revIdx;
                    const when = h.at ? new Date(h.at).toLocaleString() : "—";
                    const preview = (h.snapshot?.title || h.snapshot?.subtitle || h.snapshot?.body || "(no title)").toString().slice(0, 60);
                    return (
                      <div key={idx} className="rounded-lg border border-[#E4E5E9] bg-[#F9FAFB] p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {h.tone && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0B5CFF]/10 text-[#0B5CFF]">
                                  {h.tone}
                                </span>
                              )}
                              <span className="text-[10px] text-[#747680]">{when}</span>
                            </div>
                            <div className="text-xs text-[#0B0D17] truncate mt-1" title={preview}>{preview}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => onRestoreAiVersion(idx)}
                            className="shrink-0 h-7 px-2 rounded-md bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[10px] font-semibold text-[#0B0D17] inline-flex items-center gap-1"
                            title="Restore this version"
                            data-testid={`ai-history-restore-${idx}`}
                          >
                            <Undo2 className="w-3 h-3" strokeWidth={2} /> Restore
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E4E5E9] grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onSaveAsTemplate}
              className="text-[11px] font-semibold py-2 rounded-lg bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17] inline-flex items-center justify-center gap-1.5"
              data-testid="inspector-save-template"
              title="Save as template"
            >
              <Save className="w-3 h-3" strokeWidth={1.75} /> Save
            </button>
            <button
              type="button"
              onClick={onDuplicate}
              className="text-[11px] font-semibold py-2 rounded-lg bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17] inline-flex items-center justify-center gap-1.5"
              data-testid="inspector-duplicate"
            >
              <Copy className="w-3 h-3" strokeWidth={1.75} /> Dup
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-[11px] font-semibold py-2 rounded-lg bg-white border border-[#E4E5E9] hover:border-[#B00020] hover:text-[#B00020] text-[#0B0D17] inline-flex items-center justify-center gap-1.5"
              data-testid="inspector-delete"
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.75} /> Del
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="px-4 py-3 border-b border-[#E4E5E9]">
            <div className="vp-label" style={{ fontSize: "0.65rem" }}>Page settings</div>
            <div className="font-semibold text-sm text-[#0B0D17]">Global</div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Page title</span>
              <input
                type="text"
                className="vp-input text-sm"
                value={page.title || ""}
                onChange={(e) => onPageChange({ title: e.target.value })}
                data-testid="inspector-page-title"
              />
            </label>

            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Accent color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-14 rounded-md border border-[#E4E5E9] p-0.5 bg-white cursor-pointer"
                  value={page.accent_color || "#0B5CFF"}
                  onChange={(e) => onPageChange({ accent_color: e.target.value })}
                  data-testid="inspector-accent"
                />
                <input
                  type="text"
                  className="vp-input text-sm flex-1 font-mono"
                  value={page.accent_color || "#0B5CFF"}
                  onChange={(e) => onPageChange({ accent_color: e.target.value })}
                />
              </div>
            </label>

            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Theme</span>
              <select
                className="vp-input text-sm"
                value={page.theme || "light"}
                onChange={(e) => onPageChange({ theme: e.target.value })}
                data-testid="inspector-theme"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>

            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Font family</span>
              <select
                className="vp-input text-sm"
                value={page.font_family || "Inter"}
                onChange={(e) => onPageChange({ font_family: e.target.value })}
                style={{ fontFamily: page.font_family || "Inter" }}
                data-testid="inspector-font-family"
              >
                {CURATED_FONTS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Font scale</span>
              <div className="grid grid-cols-3 gap-1.5" data-testid="inspector-font-scale">
                {SIZE_SCALES.map((s) => (
                  <button
                    key={s.k}
                    type="button"
                    onClick={() => onPageChange({ font_scale: s.k })}
                    className={"h-8 text-xs rounded-md border font-semibold " +
                      ((page.font_scale || "m") === s.k
                        ? "bg-[#0B5CFF] text-white border-[#0B5CFF]"
                        : "bg-white text-[#0B0D17] border-[#E4E5E9] hover:border-[#0B5CFF]")}
                  >
                    {s.label.split(" ")[0].toUpperCase()}
                  </button>
                ))}
              </div>
            </label>

            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Slug</span>
              <div className="flex items-center gap-2 text-xs text-[#747680]">
                <span className="font-mono">/p/{page.slug}</span>
              </div>
            </label>

            <button
              type="button"
              onClick={onOpenSeo}
              className="w-full mt-2 text-xs font-semibold py-2 rounded-lg bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17]"
              data-testid="inspector-open-seo"
            >
              SEO & social preview →
            </button>

            <div className="pt-4 border-t border-[#E4E5E9] text-xs text-[#747680] leading-relaxed">
              Click any section on the canvas to edit it here. Drag blocks from the left palette to add new sections.
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
